import type { DocumentNode, DocumentPickerSource } from './DocumentPicker.types'

/** What the picker shows behind a node's info icon. */
export interface NodeNotice {
  summary: string
  /** Shown as a code block under the summary: a command to run. */
  command?: string
}

/** What a provider may need to word a notice. */
export interface NodeNoticeContext {
  /** The source identity from DescribeSource (SharePoint: the site URL). */
  identity: string
  /** The receptor's credentials JSON, keys lower-cased. */
  credentials: Record<string, string>
}

/**
 * Per-receptor-model presentation and key building. The backend lists every
 * provider the same way; what differs is vocabulary and the exact strings
 * folder scans and document links are stored under, which must stay
 * byte-identical to what the legacy pickers produced so existing rows keep
 * matching.
 */
export interface DocumentSourceSpec {
  modelId: string
  /** Vendor name as shown to users. */
  label: string
  /** What the top level is called: "Drives", "Document libraries", "Spaces". */
  rootCaption: string
  /** The caption for what the roots turned out to be, when it varies. */
  rootCaptionFor?: (roots: DocumentNode[]) => string
  /** Noun for a container-like node of this provider. */
  containerNoun: (node: DocumentNode) => string
  /**
   * Whether a container-like node can be a folder scan root. Navigation-only
   * nodes (a site, the list of sites) are browsed through, never picked.
   */
  isScanRoot: (node: DocumentNode) => boolean
  /** Hint telling users whom to share documents with so the receptor sees them. */
  shareHint: (identity: string) => string
  /**
   * A notice for a node the user can do something about, such as a site the
   * connection was not granted; undefined for an ordinary node.
   */
  nodeNotice: (
    node: DocumentNode,
    ctx: NodeNoticeContext,
  ) => NodeNotice | undefined
  /**
   * What to tell the user when the provider refused a listing outright
   * (atRoot: the connection's own roots); undefined keeps the picker's
   * generic "share it with the identity" wording.
   */
  noAccessNotice?: (
    ctx: NodeNoticeContext,
    atRoot: boolean,
  ) => NodeNotice | undefined
  /** root_key stored on a folder scan for this node. */
  folderScanRootKey: (node: DocumentNode) => string
  /** root_path (display) stored on a folder scan for this node. */
  folderScanRootPath: (node: DocumentNode) => string
  /** Linker.SetLink import key for a picked document. */
  linkImportKey: (node: DocumentNode, source: DocumentPickerSource) => string
}

/** The Trustero permission script invocation that grants a site's folders. */
const sharepointGrantCommand = (
  siteUrl: string,
  credentials: Record<string, string>,
): string => {
  const appId = credentials.clientid || '<application (client) ID>'
  return `.\\sharepoint-permission.ps1 -SiteUrl "${siteUrl}" -AppId "${appId}" -folderPaths "/<folder>" -DriveName "Documents"`
}

/**
 * Receptor models the DocumentSource service can browse today. Entry points
 * send these models to the document picker and leave the rest on their legacy
 * pickers; a model joins the list when its backend provider lands.
 */
export const DOCUMENT_SOURCE_MODEL_IDS: readonly string[] = [
  'trr-gdrive',
  'trr-sharepoint',
  'trr-confluence-dms',
  'trr-notion',
]

export const hasDocumentSourceProvider = (modelId: string): boolean =>
  DOCUMENT_SOURCE_MODEL_IDS.includes(modelId)

const byNodeTitle = (node: DocumentNode): string =>
  node.title || node.url || node.id

const specs: Record<string, DocumentSourceSpec> = {
  'trr-gdrive': {
    modelId: 'trr-gdrive',
    label: 'Google Drive',
    rootCaption: 'Drives',
    containerNoun: (node) =>
      node.kind === 'container' ? 'shared drive' : 'folder',
    isScanRoot: () => true,
    shareHint: (identity) =>
      identity
        ? `Showing what ${identity} can read. Share folders with that account to see them here.`
        : 'Showing what the connected service account can read.',
    nodeNotice: () => undefined,
    folderScanRootKey: (node) => node.id,
    folderScanRootPath: byNodeTitle,
    linkImportKey: (node, source) => `${node.id}:${source}`,
  },
  'trr-sharepoint': {
    modelId: 'trr-sharepoint',
    label: 'SharePoint',
    rootCaption: 'Document libraries',
    // Sites are the roots only when the connection names other sites.
    rootCaptionFor: (roots) =>
      roots.some((r) => r.providerType === 'site')
        ? 'Sites'
        : 'Document libraries',
    containerNoun: (node) => {
      switch (node.providerType) {
        case 'site':
          return 'site'
        case 'library':
          return 'document library'
        default:
          return 'folder'
      }
    },
    // Sites are browsed through, never scanned.
    isScanRoot: (node) => node.providerType !== 'site',
    // The identity is the configured site URL: SharePoint is browsed as the
    // receptor's app registration, which has whatever the sites grant it.
    shareHint: (identity) =>
      identity
        ? `Showing what the connected app registration can read in ${identity}.`
        : 'Showing what the connected app registration can read.',
    // A site from the connection's "Other sites" that Graph refused: the
    // app registration has no grant on it. Folder grants are made with
    // Trustero's permission script, so hand the user the exact command.
    nodeNotice: (node, { credentials }) => {
      if (node.providerType !== 'site' || node.hasChildren || !node.url) {
        return undefined
      }
      return {
        summary:
          'The connected app registration has no access to this site yet. Grant it the folders to scan with the Trustero SharePoint permission script (github.com/trustero/sharepoint), then reopen the picker:',
        command: sharepointGrantCommand(node.url, credentials),
      }
    },
    // The app registration activates on a token alone, so a connection whose
    // site was never granted (the permission script not run yet) opens to a
    // refused root listing. Say so, with the command, instead of the Drive
    // wording that would tell the user to share the site with its own URL.
    noAccessNotice: ({ identity, credentials }, atRoot) => ({
      summary: atRoot
        ? `The connected app registration has no access to ${
            identity || 'the configured site'
          } yet. Grant it the folders to scan with the Trustero SharePoint permission script (github.com/trustero/sharepoint), then retry:`
        : 'The connected app registration has no access to this folder. Grant it with the Trustero SharePoint permission script (github.com/trustero/sharepoint), then retry:',
      command: sharepointGrantCommand(identity || '<site URL>', credentials),
    }),
    folderScanRootKey: (node) => node.id,
    folderScanRootPath: byNodeTitle,
    linkImportKey: (node, source) => `${node.id}:${source}`,
  },
  'trr-confluence-dms': {
    modelId: 'trr-confluence-dms',
    label: 'Confluence',
    rootCaption: 'Spaces',
    rootCaptionFor: (roots) =>
      roots.some((r) => r.kind === 'label') ? 'Labels' : 'Spaces',
    containerNoun: (node) =>
      node.kind === 'container'
        ? 'space'
        : node.kind === 'label'
        ? 'label'
        : node.kind === 'folder'
        ? 'folder'
        : 'page',
    // A label groups pages and is browsed through; a space, a folder or a
    // page can be scanned.
    isScanRoot: (node) => node.kind !== 'label',
    nodeNotice: () => undefined,
    // A service account is worth naming: pages must be shared with it. An
    // OAuth connection reads as the person who connected it, and the
    // identity is then just the site host, which says nothing useful.
    shareHint: (identity) =>
      identity.includes('@')
        ? `Showing what ${identity} can read in Confluence. Share pages with that account to see them here.`
        : '',
    folderScanRootKey: (node) => node.id,
    folderScanRootPath: byNodeTitle,
    linkImportKey: (node, source) => `${node.spaceId}:${node.id}:${source}`,
  },
  'trr-notion': {
    modelId: 'trr-notion',
    label: 'Notion',
    // The roots are the top of what was shared with the connection: pages
    // and databases (listed as their tables).
    rootCaption: 'Shared pages',
    rootCaptionFor: (roots) =>
      roots.length > 0 && roots.every((r) => r.providerType === 'data_source')
        ? 'Shared databases'
        : 'Shared pages',
    containerNoun: (node) =>
      node.providerType === 'data_source' ? 'database' : 'page',
    // A page or a database table can be scanned.
    isScanRoot: () => true,
    nodeNotice: () => undefined,
    // The identity is the connection bot and its workspace; pages must be
    // shared with that connection under Content access.
    shareHint: (identity) =>
      identity
        ? `Showing what the ${identity} connection can read. Share pages, teamspaces or databases with it under Content access to see them here.`
        : 'Showing what the Trustero connection can read in Notion. Share pages, teamspaces or databases with it under Content access to see them here.',
    folderScanRootKey: (node) => node.id,
    folderScanRootPath: byNodeTitle,
    linkImportKey: (node, source) => `${node.id}:${source}`,
  },
}

const genericSpec = (modelId: string): DocumentSourceSpec => ({
  modelId,
  label: 'Documents',
  rootCaption: 'Locations',
  containerNoun: () => 'folder',
  isScanRoot: () => true,
  nodeNotice: () => undefined,
  shareHint: (identity) =>
    identity ? `Showing what ${identity} can read.` : '',
  folderScanRootKey: (node) => node.id,
  folderScanRootPath: byNodeTitle,
  linkImportKey: (node, source) => `${node.id}:${source}`,
})

export const documentSourceSpec = (modelId: string): DocumentSourceSpec =>
  specs[modelId] || genericSpec(modelId)
